import * as React from 'react'

import { Temporal } from '@js-temporal/polyfill'

import { format, isToday } from '~publish/helpers/temporal'
import { useTimezone } from '~publish/hooks/useTimezone'
import { useTwentyFourHourTimeFormat } from '~publish/hooks/useTwentyFourHourTimeFormat'

/**
 * Formats a post's `dueAt` timestamp for display in miniature cards.
 *
 * - Today → time only (e.g. "2:30 PM" or "14:30")
 * - Other days → abbreviated date + time (e.g. "Feb 18, 2:30 PM")
 *
 * Respects the user's timezone and 24-hour preference.
 */
export function useFormattedPostTime(dueAt: string | null | undefined): string {
  const timeZone = useTimezone()
  const is24Hour = useTwentyFourHourTimeFormat()

  return React.useMemo(() => {
    if (!dueAt) return ''
    const zonedDueAt = Temporal.Instant.from(dueAt).toZonedDateTimeISO(timeZone)

    if (isToday(zonedDueAt)) {
      return format(zonedDueAt, is24Hour ? 'HH:mm' : 'h:mm a')
    }
    return format(zonedDueAt, is24Hour ? 'MMM d, HH:mm' : 'MMM d, h:mm a')
  }, [dueAt, is24Hour, timeZone])
}
